import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import {
  NON_REFUNDABLE_PROCESSING_FEE_NOTICE,
  NON_REFUNDABLE_PROCESSING_FEE_REFUND_NOTE,
} from "../../utils/transactionPolicyText";
import { reservePaymentTab, sendReservedTabToPayment } from "../../utils/paymentTab";
const CUSTOMER_REQUESTED_CANCELLATION_REASON = "Customer requested cancellation";

function createInitialProductState() {
  return {
    orderNumber: "",
    orderEmail: "",
    phoneLast4: "",
    reason: "",
    description: "",
    lookupLoading: false,
    submitting: false,
    casesLoading: false,
    error: "",
    successMessage: "",
    order: null,
    selectedItems: {},
    itemDetailsById: {},
    conditionAttestation: {
      itemUsedStatus: "unknown",
      packageOpenedStatus: "unknown",
      itemDamagedStatus: "unknown",
      reasonDetail: "",
    },
    cases: [],
    duplicateCase: null,
    lookupNotice: "",
    submissionMode: "single",
    itemLocks: {},
    eligibility: null,
    lookupIneligibleMessage: "",
    transactionContext: null,
  };
}

function formatBookingCalendarDate(appointmentDate) {
  const value = String(appointmentDate || "").trim();
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value;

  const [, year, month, day] = match;
  const localDate = new Date(Number(year), Number(month) - 1, Number(day));
  return localDate.toLocaleDateString();
}

/** "14:30" -> "2:30 PM". Used by the group cancellation picker. */
function formatClockTime(time24) {
  const value = String(time24 || "").trim();
  const match = value.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return value;

  const hour = Number(match[1]);
  const ampm = hour >= 12 ? "PM" : "AM";
  return `${hour % 12 || 12}:${match[2]} ${ampm}`;
}

function upsertReturnCase(cases, incomingCase) {
  if (!incomingCase) {
    return Array.isArray(cases) ? cases : [];
  }

  const safeCases = Array.isArray(cases) ? cases : [];
  const incomingId = String(incomingCase._id || incomingCase.caseId || "");
  const incomingNumber = String(incomingCase.caseNumber || "");

  const existingIndex = safeCases.findIndex((row) => {
    const rowId = String(row?._id || row?.caseId || "");
    const rowNumber = String(row?.caseNumber || "");
    return (incomingId && rowId && rowId === incomingId) || (incomingNumber && rowNumber && rowNumber === incomingNumber);
  });

  if (existingIndex < 0) {
    return [incomingCase, ...safeCases];
  }

  const nextCases = [...safeCases];
  nextCases[existingIndex] = {
    ...nextCases[existingIndex],
    ...incomingCase,
  };
  return nextCases;
}

function mapReturnCaseRow(row) {
  if (!row) return null;

  return {
    _id: row.caseId || row._id || "",
    caseNumber: row.caseNumber || "",
    status: row.status || "requested",
    requestedAt: row.requestedAt,
    financial: {
      amountRequested: Number(row.financial?.amountRequested || row.amountRequested || 0),
      amountRequestedPreTax: Number(row.financial?.amountRequestedPreTax || row.amountRequestedPreTax || 0),
      amountRequestedTax: Number(row.financial?.amountRequestedTax || row.amountRequestedTax || 0),
      amountRequestedGross: Number(row.financial?.amountRequestedGross || row.amountRequestedGross || 0),
      stripeFeeDeduction: Number(row.financial?.stripeFeeDeduction || row.stripeFeeDeduction || 0),
    },
  };
}

function GuestRefundRequest() {
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("ogestyle_token") || "";
  const isSignedIn = Boolean(token);
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState("service");

  // Service cancellation state (kept from pre-unified behavior)
  const [bookingNumber, setBookingNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [booking, setBooking] = useState(null);
  const [shop, setShop] = useState(null);
  const [refundInfo, setRefundInfo] = useState(null);
  /**
   * 22G — the package warning the server sends with a booking that spent package sessions, and what the guest
   * wants done about the sessions it will lose. `refund` is the default because it is what a card booking does:
   * a guest who ignores this still gets their money back rather than losing both the sessions and the value.
   */
  const [packageCancellation, setPackageCancellation] = useState(null);
  /**
   * KEEP OR LOSE THE SESSIONS — and it starts UNSELECTED (user, 2026-10-17): *"its important to let them
   * intentionally make this selection to avoid any issues arising from I wasn't aware my sessions will be
   * lost"*. The old default was "refund", so a guest who never read the block had already chosen the outcome
   * that loses the sessions. Nothing is chosen now until they choose it, and Cancel stays disabled until then.
   */
  const [packageChoice, setPackageChoice] = useState("");
  /** 22G — whether they want to pay at the end of this cancellation or later (user, 2026-10-14). */
  const [packagePayTiming, setPackagePayTiming] = useState("now");
  /**
   * WHICH HELD SESSIONS THIS PAYMENT IS FOR (user, 2026-10-16): `this` = the sessions this cancellation loses,
   * `all` = those plus anything else already held on the package. Both figures are shown before they choose, and
   * the ids that were SHOWN are the ids that get priced, so the charge matches the figure either way.
   */
  const [packagePayScope, setPackagePayScope] = useState("this");
  /**
   * 22G — EVERY BOOKING THIS GUEST CAN TICK, PRICED BY THE SERVER (keyed by booking id).
   *
   * The same reason the customer's dialog has it: a group's loss depends on WHAT is ticked, and the figure for
   * the one booking that was looked up described the wrong loss. The policy and the frozen snapshot stay on the
   * server; this page only adds the figures it was handed.
   */
  const [packageCancellations, setPackageCancellations] = useState({});
  // Live members of this booking's group (anchor first), returned by the by-number
  // lookup. Empty for a booking that is not in a group.
  const [groupMembers, setGroupMembers] = useState([]);
  const [cancelSelection, setCancelSelection] = useState([]);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [customerEmail, setCustomerEmail] = useState("");
  const [phoneLast4, setPhoneLast4] = useState("");
  // The CAUSE of the cancellation, chosen by the customer (2026-10-24). Only customer causes are offered —
  // the server rejects anything else from this door, so a fee-free "force majeure" cannot be self-selected.
  const [cancelReasonCategory, setCancelReasonCategory] = useState(
    "customer_requested",
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  // Product return state
  const [productState, setProductState] = useState(createInitialProductState);
  const [serviceTransactionContext, setServiceTransactionContext] = useState(null);
  const [serviceMixedAction, setServiceMixedAction] = useState("");
  const [productMixedAction, setProductMixedAction] = useState("");

  useEffect(() => {
    const tabParam = String(
      searchParams.get("tab") || searchParams.get("mode") || "",
    )
      .trim()
      .toLowerCase();
    const prefillEmail = String(searchParams.get("email") || "")
      .trim()
      .toLowerCase();
    const prefillOrder = String(
      searchParams.get("order") || searchParams.get("orderNumber") || "",
    )
      .trim()
      .toUpperCase();

    if (["product", "product-return", "product_return"].includes(tabParam)) {
      setMode("product");
    }

    if (!prefillEmail && !prefillOrder) {
      return;
    }

    setProductState((prev) => ({
      ...prev,
      orderEmail: prefillEmail || prev.orderEmail,
      orderNumber: prefillOrder || prev.orderNumber,
    }));
  }, [searchParams]);

  const updateProduct = (patch) => {
    setProductState((prev) => ({ ...prev, ...patch }));
  };

  const productItems = useMemo(() => {
    if (!productState.order || !Array.isArray(productState.order.shops)) {
      return [];
    }

    return productState.order.shops.flatMap((shopRow) => {
      const shopName = shopRow.shopName || "Shop";
      const shopLocationName = shopRow.shopId?.name || shopName;
      const shopAddress = [
        shopRow.shopId?.street || shopRow.shopId?.address || "",
        [shopRow.shopId?.city || "", shopRow.shopId?.state || "", shopRow.shopId?.zipCode || shopRow.shopId?.zip || ""]
          .filter(Boolean)
          .join(", "),
      ]
        .filter(Boolean)
        .join(" · ");
      return (shopRow.items || []).map((item) => ({
        shopName,
        shopLocationName,
        shopAddress,
        name: item.name || "Product",
        quantity: Number(item.quantity || 0),
        priceAtPurchase: Number(item.priceAtPurchase || 0),
        estimatedTax: Number(item.estimatedTax || 0),
        stripeFeeDeduction: Number(item.stripeFeeDeduction || 0),
        expectedRefundAmount: Number(item.expectedRefundAmount || 0),
        orderItemId: item.orderItemId || "",
        returnState: item.returnState || "none",
        activeReturnCaseId: item.activeReturnCaseId || null,
        sellerRole: item.sellerRole || "",
        shopTimezone: shopRow.shopId?.timezone || "America/Chicago",
      }));
    });
  }, [productState.order]);

  const groupedProductItems = useMemo(() => {
    const groups = new Map();
    for (const item of productItems) {
      const key = `${item.shopName}::${item.shopLocationName}::${item.shopAddress}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          shopName: item.shopName,
          shopLocationName: item.shopLocationName,
          shopAddress: item.shopAddress,
          items: [],
        });
      }
      groups.get(key).items.push(item);
    }
    return Array.from(groups.values());
  }, [productItems]);

  const selectedReturnItems = useMemo(() => {
    const selectedMap = productState.selectedItems || {};
    return productItems
      .filter((item) => Boolean(selectedMap[item.orderItemId]))
      .map((item) => ({
        orderItemId: item.orderItemId,
        quantity: item.quantity,
        reason:
          productState.submissionMode === "batch"
            ? productState.itemDetailsById?.[item.orderItemId]?.reason || ""
            : productState.reason,
        description:
          productState.submissionMode === "batch"
            ? productState.itemDetailsById?.[item.orderItemId]?.description || ""
            : productState.description,
        conditionAttestation:
          productState.submissionMode === "batch"
            ? productState.itemDetailsById?.[item.orderItemId]?.conditionAttestation || {
                itemUsedStatus: "unknown",
                packageOpenedStatus: "unknown",
                itemDamagedStatus: "unknown",
                reasonDetail: "",
              }
            : {
                ...(productState.conditionAttestation || {}),
              },
      }))
      .filter((entry) => entry.orderItemId && entry.quantity > 0);
  }, [
    productItems,
    productState.selectedItems,
    productState.submissionMode,
    productState.itemDetailsById,
    productState.reason,
    productState.description,
    productState.conditionAttestation,
  ]);

  const isDamagedOnlyPolicy =
    productState.eligibility?.returnCondition === "damaged_only";

  const getItemLock = (orderItemId) => productState.itemLocks?.[orderItemId] || null;

  const isItemSelectionLocked = (orderItemId) => {
    const lock = getItemLock(orderItemId);
    return Boolean(lock?.isLocked);
  };

  const getItemReturnBadge = (item) => {
    const lock = getItemLock(item.orderItemId);
    if (lock?.status) {
      return {
        label: String(lock.status).replace(/_/g, " "),
        locked: Boolean(lock.isLocked),
        caseNumber: lock.caseNumber || "",
      };
    }

    if (item.returnState && item.returnState !== "none") {
      return {
        label: String(item.returnState).replace(/_/g, " "),
        locked: ["return_pending", "return_under_review", "return_approved_for_refund", "return_refunded"].includes(item.returnState),
        caseNumber: "",
      };
    }

    return null;
  };

  const updateItemDetails = (orderItemId, patch) => {
    setProductState((prev) => ({
      ...prev,
      itemDetailsById: {
        ...(prev.itemDetailsById || {}),
        [orderItemId]: {
          reason: "",
          description: "",
          conditionAttestation: {
            itemUsedStatus: "unknown",
            packageOpenedStatus: "unknown",
            itemDamagedStatus: "unknown",
            reasonDetail: "",
          },
          ...(prev.itemDetailsById?.[orderItemId] || {}),
          ...patch,
        },
      },
    }));
  };

  const formatReturnDeadline = (deadlineValue) => {
    if (!deadlineValue) return "";
    const date = new Date(deadlineValue);
    if (Number.isNaN(date.getTime())) return "";

    const timezone =
      selectedReturnItems
        .map((entry) =>
          productItems.find((item) => String(item.orderItemId) === String(entry.orderItemId)),
        )
        .find((item) => item?.shopTimezone)?.shopTimezone ||
      productItems.find((item) => item?.shopTimezone)?.shopTimezone ||
      "America/Chicago";

    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: timezone,
      timeZoneName: "short",
    });
  };

  const resetServiceFlow = () => {
    setError("");
    setBookingNumber("");
    setBooking(null);
    setShop(null);
    setRefundInfo(null);
    setGroupMembers([]);
    setCancelSelection([]);
    setServiceTransactionContext(null);
    setServiceMixedAction("");
    setShowRequestForm(false);
    setCustomerEmail("");
    setPhoneLast4("");
    setSubmitting(false);
    setSubmitSuccess(false);
    setSubmitResult(null);
  };

  const resetProductFlow = (message = "") => {
    setProductState({
      ...createInitialProductState(),
      successMessage: message,
    });
    setProductMixedAction("");
  };

  const submitGuestProductReturnDirect = async ({
    orderNumber,
    email,
    phoneLast4: submittedPhoneLast4,
    reason,
    description = "",
    items,
    submissionMode = "single",
    conditionAttestation = null,
    suppressCustomerEmail = false,
  }) => {
    return axios.post(`${baseURL}/api/product-returns/guest/cases/direct`, {
      orderNumber: String(orderNumber || "").trim().toUpperCase(),
      email: String(email || "").trim().toLowerCase(),
      phoneLast4: String(submittedPhoneLast4 || "").replace(/\D/g, "").slice(-4),
      submissionMode,
      reason: String(reason || "").trim(),
      description: String(description || "").trim(),
      items: Array.isArray(items) ? items : [],
      suppressCustomerEmail,
      conditionAttestation: conditionAttestation || {
        itemUsedStatus: "unknown",
        packageOpenedStatus: "unknown",
        itemDamagedStatus: "unknown",
        reasonDetail: String(description || reason || "").trim(),
      },
    });
  };

  const notifyMixedTransactionAction = async ({
    source,
    orderNumber,
    bookingNumber: submittedBookingNumber,
    email,
    phoneLast4: submittedPhoneLast4,
    productResponse,
    bookingResponse,
  }) => {
    const responseCases = Array.isArray(productResponse?.data?.cases)
      ? productResponse.data.cases
      : [];
    const productCaseNumbers = responseCases
      .map((row) => row?.caseNumber)
      .filter(Boolean);
    if (!productCaseNumbers.length && productResponse?.data?.case?.caseNumber) {
      productCaseNumbers.push(productResponse.data.case.caseNumber);
    }
    const productReturnAmount = responseCases.length
      ? responseCases.reduce((sum, row) => sum + Number(row?.amountRequested || row?.financial?.amountRequested || 0), 0)
      : Number(productResponse?.data?.case?.amountRequested || productResponse?.data?.case?.financial?.amountRequested || 0);
    const bookingRefundAmount = Number(
      bookingResponse?.data?.refundAmount ||
      bookingResponse?.data?.refund?.refundAmount ||
      0,
    );

    try {
      await axios.post(
        `${baseURL}/api/product-returns/mixed/notify`,
        {
          source,
          orderNumber,
          bookingNumber: submittedBookingNumber,
          email: String(email || "").trim().toLowerCase(),
          phoneLast4: String(submittedPhoneLast4 || "").replace(/\D/g, "").slice(-4),
          productCaseNumbers,
          productReturnAmount: Number(productReturnAmount.toFixed(2)),
          bookingRefundAmount: Number(bookingRefundAmount.toFixed(2)),
        },
        isSignedIn ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );
    } catch (_notificationErr) {}
  };

  const resolveSelectedProductReturnPayload = () => {
    return {
      orderNumber: productState.order?.orderNumber || productState.orderNumber,
      email: productState.orderEmail,
      phoneLast4: productState.phoneLast4,
      submissionMode: productState.submissionMode || "single",
      reason:
        productState.submissionMode === "batch"
          ? String(selectedReturnItems[0]?.reason || "").trim()
          : productState.reason.trim(),
      description:
        productState.submissionMode === "batch"
          ? String(selectedReturnItems[0]?.description || "").trim()
          : productState.description.trim(),
      items: selectedReturnItems,
      conditionAttestation: {
        itemUsedStatus: productState.conditionAttestation?.itemUsedStatus || "unknown",
        packageOpenedStatus:
          productState.conditionAttestation?.packageOpenedStatus || "unknown",
        itemDamagedStatus:
          productState.conditionAttestation?.itemDamagedStatus || "unknown",
        reasonDetail:
          productState.conditionAttestation?.reasonDetail?.trim() ||
          productState.description.trim(),
      },
    };
  };

  const submitServiceAndProductTogether = async () => {
    const linkedOrderNumber = serviceTransactionContext?.linkedOrderNumber || "";
    const submittedEmail = customerEmail.trim().toLowerCase();
    const submittedPhoneLast4 = phoneLast4.replace(/\D/g, "").slice(-4);

    if (!linkedOrderNumber) {
      setError("This booking is linked to a product order, but the order number is not available.");
      return;
    }

    if (!submittedEmail || submittedPhoneLast4.length !== 4) {
      setError("Please enter the booking email and phone last 4.");
      return;
    }

    setError("");
    setSubmitting(true);

    try {
      const bookingPayload = {
        reason: CUSTOMER_REQUESTED_CANCELLATION_REASON,
        bookingNumber: booking.bookingNumber,
        email: submittedEmail,
        phoneLast4: submittedPhoneLast4,
        suppressCustomerEmail: true,
        ...cancelScopePayload(),
      };

      const bookingResponse = await axios.post(
        `${baseURL}/api/bookings/${booking._id}/customer-cancel`,
        bookingPayload,
      );

      const productItems = (serviceTransactionContext?.productSummary || [])
        .map((item) => ({
          orderItemId: item.orderItemId,
          quantity: item.quantity || 1,
          reason: CUSTOMER_REQUESTED_CANCELLATION_REASON,
          description: `Requested with booking cancellation ${booking.bookingNumber || ""}`.trim(),
          conditionAttestation: {
            itemUsedStatus: "unknown",
            packageOpenedStatus: "unknown",
            itemDamagedStatus: "unknown",
            reasonDetail: CUSTOMER_REQUESTED_CANCELLATION_REASON,
          },
        }))
        .filter((item) => item.orderItemId);

      if (!productItems.length) {
        setSubmitResult({
          ...bookingResponse.data,
          mixedTransactionPartial: true,
          mixedTransactionMessage:
            "Booking cancelled, but no product item IDs were available to create the return automatically.",
        });
        setSubmitSuccess(true);
        return;
      }

      const productResponse = await submitGuestProductReturnDirect({
        orderNumber: linkedOrderNumber,
        email: submittedEmail,
        phoneLast4: submittedPhoneLast4,
        submissionMode: productItems.length > 1 ? "batch" : "single",
        reason: CUSTOMER_REQUESTED_CANCELLATION_REASON,
        description: `Requested with booking cancellation ${booking.bookingNumber || ""}`.trim(),
        items: productItems,
        conditionAttestation: {
          itemUsedStatus: "unknown",
          packageOpenedStatus: "unknown",
          itemDamagedStatus: "unknown",
          reasonDetail: CUSTOMER_REQUESTED_CANCELLATION_REASON,
        },
        suppressCustomerEmail: true,
      });

      await notifyMixedTransactionAction({
        source: "booking",
        orderNumber: linkedOrderNumber,
        bookingNumber: booking.bookingNumber,
        email: submittedEmail,
        phoneLast4: submittedPhoneLast4,
        productResponse,
        bookingResponse,
      });

      setSubmitResult({
        ...bookingResponse.data,
        mixedProductReturn: productResponse.data,
        mixedTransactionMessage: "Booking cancellation and linked product return were submitted.",
      });
      setSubmitSuccess(true);
      setCustomerEmail("");
      setPhoneLast4("");
      setShowRequestForm(false);
      setTimeout(() => {
        setSubmitSuccess(false);
        setSubmitResult(null);
        setBooking(null);
        setShop(null);
        setRefundInfo(null);
        setServiceTransactionContext(null);
        setBookingNumber("");
      }, 8000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Unable to submit the mixed cancellation request.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitProductAndServiceTogether = async () => {
    const linkedBooking = productState.transactionContext?.linkedBookings?.find((row) => row?.bookingId || row?.bookingNumber);
    const productPayload = resolveSelectedProductReturnPayload();
    const submittedEmail = String(productPayload.email || "").trim().toLowerCase();
    const submittedPhoneLast4 = String(productPayload.phoneLast4 || "").replace(/\D/g, "").slice(-4);

    if (!linkedBooking?.bookingId) {
      updateProduct({ error: "This order is linked to a booking, but the booking ID is not available." });
      return;
    }

    if (linkedBooking.cancellationEligibility && !linkedBooking.cancellationEligibility.canRequestRefund) {
      updateProduct({ error: linkedBooking.cancellationEligibility.reason || "The linked booking cannot be cancelled online." });
      return;
    }

    if (!isSignedIn && (!submittedEmail || submittedPhoneLast4.length !== 4)) {
      updateProduct({ error: "Please enter the order email and last 4 digits of your phone number." });
      return;
    }

    if (!productPayload.reason || !productPayload.items.length) {
      updateProduct({ error: "Select at least one product and provide a return reason." });
      return;
    }

    updateProduct({ submitting: true, error: "", successMessage: "" });

    try {
      const productResponse = isSignedIn
        ? await axios.post(`${baseURL}/api/product-returns/cases`, {
            ...productPayload,
            suppressCustomerEmail: true,
          }, {
            headers: { Authorization: `Bearer ${token}` },
          })
        : await submitGuestProductReturnDirect({
            ...productPayload,
            email: submittedEmail,
            phoneLast4: submittedPhoneLast4,
            suppressCustomerEmail: true,
          });

      const bookingResponse = await axios.post(
        `${baseURL}/api/bookings/${linkedBooking.bookingId}/customer-cancel`,
        {
          reason: productPayload.reason,
          bookingNumber: linkedBooking.bookingNumber,
          email: submittedEmail,
          phoneLast4: submittedPhoneLast4,
          suppressCustomerEmail: true,
          cancellationReasonCategory: cancelReasonCategory,
          // ── 22G: THE PACKAGE CHOICE, WHICH THIS PATH USED TO DROP ──────────────────────────────
          //
          // Deliberately the CHOICE only, not `cancelScopePayload()`. That builder scopes a cancellation to the
          // bookings ticked for the SERVICE lookup, and this call cancels the booking the ORDER is linked to —
          // which can be a different one. Sending its `cancelBookingIds` here would cancel a selection made for
          // another booking, so the group scope stays untouched and only the customer's answer travels.
          //
          // It is sent only when the warning was actually loaded for a booking, so the paths where nothing was
          // asked send nothing and settle as a refund — the safe default, and never a silent reversal of an
          // answer the customer never gave.
          ...(packageCancellation ? { packageCancellationChoice: packageChoice } : {}),
        },
        isSignedIn ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );

      await notifyMixedTransactionAction({
        source: "product",
        orderNumber: productPayload.orderNumber,
        bookingNumber: linkedBooking.bookingNumber,
        email: submittedEmail || productPayload.email,
        phoneLast4: submittedPhoneLast4,
        productResponse,
        bookingResponse,
      });

      const caseNumber = productResponse.data?.case?.caseNumber || "";
      resetProductFlow(
        caseNumber
          ? `Product return ${caseNumber} submitted and linked booking cancelled.`
          : "Product return submitted and linked booking cancelled.",
      );
    } catch (err) {
      const duplicateCase = err.response?.data?.existingCase || null;
      const duplicateCaseRow = duplicateCase ? mapReturnCaseRow(duplicateCase) : null;
      setProductState((prev) => ({
        ...prev,
        submitting: false,
        error: err.response?.data?.message || err.message || "Unable to submit the mixed product return request.",
        duplicateCase,
        cases: duplicateCaseRow ? upsertReturnCase(prev.cases, duplicateCaseRow) : prev.cases,
      }));
    }
  };

  const handleLookup = async (e) => {
    e.preventDefault();
    setError("");
    setBooking(null);
    setShop(null);
    setRefundInfo(null);
    setServiceTransactionContext(null);
    setServiceMixedAction("");
    setShowRequestForm(false);
    setSubmitSuccess(false);

    if (!bookingNumber.trim()) {
      setError("Please enter a booking number");
      return;
    }

    if (!customerEmail.trim()) {
      setError("Please enter the booking email");
      return;
    }

    setLoading(true);

    try {
      const lookupEmail = customerEmail.trim().toLowerCase();
      const response = await axios.get(
        `${baseURL}/api/bookings/by-number/${bookingNumber.trim()}?email=${encodeURIComponent(lookupEmail)}`,
      );

      if (response.data.success) {
        setBooking(response.data.booking);
        setShop(response.data.shop);
        setRefundInfo(response.data.refundInfo);
        // 22G: present only when this booking used a package. Absent means there is nothing to warn about, and
        // the cancel payload then carries no choice at all.
        setPackageCancellation(response.data.packageCancellation || null);
        setPackageCancellations(response.data.packageCancellations || {});
        // NOTHING IS PRE-CHOSEN — see the state declaration: the old default was the outcome that LOSES the
        // sessions, which a guest who never read the block had silently selected.
        setPackageChoice("");
        setServiceTransactionContext(response.data.transactionContext || null);
        setServiceMixedAction(response.data.transactionContext?.isMixedTransaction ? "" : "booking_only");

        // Group scope. There is no "clicked row" here - the guest looked up ONE booking
        // number, so pre-tick that booking, and if it is the anchor pre-tick the whole
        // group (which is what the server does with an anchor selection anyway).
        const members = Array.isArray(response.data.groupMembers)
          ? response.data.groupMembers
          : [];
        setGroupMembers(members);
        const lookedUpId = response.data.booking?._id;
        const lookedUpIsAnchor =
          members.find((member) => String(member._id) === String(lookedUpId))
            ?.isPrimary === true;
        setCancelSelection(
          members.length === 0
            ? []
            : lookedUpIsAnchor
              ? members.map((member) => member._id)
              : members
                  .filter((member) => String(member._id) === String(lookedUpId))
                  .map((member) => member._id),
        );

        setShowRequestForm(response.data.refundInfo.canRequestRefund);
      } else {
        setError(response.data.message || "Unable to retrieve booking");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to lookup booking. Please verify the booking number.",
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * The group cancel scope, or {} when this booking is not in a group.
   *
   * One helper so all three cancel call sites (service-only, service+product mixed,
   * and the signed-in retry) cannot disagree about the scope. An empty object keeps
   * the existing single-booking behaviour exactly as it was.
   */
  const cancelScopePayload = () => {
    // 22G — the package choice travels with EVERY cancel call site, through this one builder, so the guest's
    // answer cannot be honoured on the service-only path and dropped on the mixed one. Sent only when the server
    // sent a ladder, so a booking that used no package sends exactly the payload it always did.
    const packagePayload = packageCancellation
      ? { packageCancellationChoice: packageChoice }
      : {};

    // THE CAUSE (2026-10-24). It travels with every call site for the same reason the package choice does:
    // the server records it as the cancellation's category so the money rule — and the reporting — have a
    // real answer instead of a free-text guess. The server refuses anything a customer may not claim.
    const causePayload = { cancellationReasonCategory: cancelReasonCategory };

    if (!booking?.groupBookingId) return { ...causePayload, ...packagePayload };
    if (!Array.isArray(groupMembers) || groupMembers.length === 0) {
      return { ...causePayload, ...packagePayload };
    }
    if (cancelSelection.length === 0) return { ...causePayload, ...packagePayload };
    return { ...causePayload, ...packagePayload, cancelBookingIds: cancelSelection };
  };

  const handleCancelTargetToggle = (member) => {
    setCancelSelection((previous) => {
      if (member.isPrimary) {
        // Ticking the anchor ticks everything - members cannot stay booked once the
        // anchor is gone (their times are chained off it).
        return previous.includes(member._id)
          ? previous.filter((id) => id !== member._id)
          : groupMembers.map((entry) => entry._id);
      }
      return previous.includes(member._id)
        ? previous.filter((id) => id !== member._id)
        : [...previous, member._id];
    });
  };

  const handleSubmitRequest = async (e, mixedTransactionConfirmed = false, nextAction = "") => {
    e.preventDefault();

    if (!customerEmail.trim()) {
      setError("Please provide your email address");
      return;
    }

    if (serviceTransactionContext?.isMixedTransaction && !mixedTransactionConfirmed && !serviceMixedAction) {
      setError("Choose whether to cancel only the booking or cancel the booking and return linked products.");
      return;
    }

    setError("");
    setSubmitting(true);

    // ── 22G: THE PAYMENT WINDOW IS RESERVED BEFORE THE FIRST `await` ────────────────────────────
    // `window.open` after an await is not user-initiated and the browser blocks it; the old fallback then
    // navigated THIS tab to Stripe, which lost the page and everything on it (reported 2026-10-14). Reserved only
    // when they chose to pay now AND to keep the sessions — "pay later" opens nothing.
    const reservedTab =
      packageChoice === "redeem" && packagePayTiming === "now"
        ? reservePaymentTab()
        : null;

    try {
      const currentToken = localStorage.getItem("ogestyle_token");
      const guestPayload = {
        reason: CUSTOMER_REQUESTED_CANCELLATION_REASON,
        bookingNumber: booking.bookingNumber,
        email: customerEmail.trim(),
        phoneLast4: phoneLast4.replace(/\D/g, "").slice(-4),
        // WHEN THEY INTEND TO PAY, so the server knows whether to DEFER the cancellation email. With "now" there
        // is a payment pending, and an email written before the card details are typed can only describe a
        // cancellation that is not yet settled (reported 2026-10-16). "later" leaves nothing pending, so its
        // email goes straight away and says the sessions are held.
        ...(packageChoice === "redeem" ? { packageCancellationPayTiming: packagePayTiming } : {}),
        ...cancelScopePayload(),
      };

      let response;

      if (currentToken) {
        try {
          response = await axios.post(
            `${baseURL}/api/bookings/${booking._id}/customer-cancel`,
            guestPayload,
            { headers: { Authorization: `Bearer ${currentToken}` } },
          );
        } catch (authErr) {
          const authStatus = authErr.response?.status;
          const authCode = authErr.response?.data?.error;

          if (authStatus === 401 && authCode === "invalid_token") {
            localStorage.removeItem("ogestyle_token");

            if (!guestPayload.phoneLast4 || guestPayload.phoneLast4.length < 4) {
              setError("Your session expired. Please enter the last 4 digits of your phone number and submit again.");
              setSubmitting(false);
              return;
            }

            response = await axios.post(
              `${baseURL}/api/bookings/${booking._id}/customer-cancel`,
              guestPayload,
            );
          } else {
            throw authErr;
          }
        }
      } else {
        if (!guestPayload.phoneLast4 || guestPayload.phoneLast4.length < 4) {
          setError("Please enter the last 4 digits of your phone number");
          setSubmitting(false);
          return;
        }
        response = await axios.post(
          `${baseURL}/api/bookings/${booking._id}/customer-cancel`,
          guestPayload,
        );
      }

      setSubmitResult(response.data);
      setSubmitSuccess(true);
      const submittedEmail = customerEmail.trim().toLowerCase();
      const submittedPhoneLast4 = phoneLast4.replace(/\D/g, "").slice(-4);
      setCustomerEmail("");
      setPhoneLast4("");
      setShowRequestForm(false);

      // ── 22G: THEY KEPT THEIR SESSIONS, SO THEY HAVE A PAYMENT TO MAKE ───────────────────────
      // Started here, before the 8-second reset below, and only when the server reports a hold was actually
      // created. A guest pays with the scoped token they were issued when they verified the package; if that
      // token is gone (expired, or they booked on a card and verified nothing) the payment cannot be started
      // here, and the honest answer is where to make it rather than a dead end.
      if (
        packageChoice === "redeem" &&
        response.data?.packageCancellation?.outcome === "held_for_redemption"
      ) {
        if (packagePayTiming === "later") {
          // PAY LATER: nothing opens, and the offer waits on the booking page. Said out loud, because a customer
          // who chose it needs to know where to find the sessions again.
          setError(
            "Your sessions are held. To restore them, open this booking on the booking page and use the “Pay to get them back” button there — your other sessions keep working in the meantime.",
          );
        } else {
          // The purchase id comes from THAT response — the settlement names it — with the warning's own copy as a
          // fallback. Passing it in rather than reading it inside keeps the payment starter independent of which
          // of the two the server happened to fill in.
          //
          // The FOURTH argument is the proof the cancellation itself just returned. Without it a guest who chose
          // "pay now" had nothing to pay with: they had never run the package challenge, so there was no token in
          // `sessionStorage` and the tab closed again empty (reported 2026-10-16).
          startGuestRedemption(
            booking?._id,
            response.data?.packageCancellation?.purchaseId ||
              packagePreview?.purchaseId ||
              packageCancellation?.purchaseId,
            reservedTab,
            response.data?.packageRedemptionToken || null,
            // "ALL" SENDS BOTH LISTS — the holds already on the package and the ones this cancellation just
            // created — so the charge equals the figure the page showed.
            packagePayScope === "all"
              ? [
                  ...(packageCancellation?.otherHeldEntryIds || []),
                  ...(response.data?.packageHoldEntryIds || []),
                ]
              : response.data?.packageHoldEntryIds || null,
          );
        }
      } else if (reservedTab) {
        // Nothing to pay: a reserved blank tab is never left behind.
        try {
          reservedTab.close();
        } catch {
          // Nothing to do.
        }
      }

      if (nextAction === "product_return") {
        const linkedOrderNumber = serviceTransactionContext?.linkedOrderNumber || "";
        setMode("product");
        setProductState((prev) => ({
          ...prev,
          orderNumber: linkedOrderNumber || prev.orderNumber,
          orderEmail: submittedEmail || prev.orderEmail,
          phoneLast4: submittedPhoneLast4 || prev.phoneLast4,
          successMessage: linkedOrderNumber
            ? "Booking cancelled. The linked order number is filled in below so you can finish the product return."
            : "Booking cancelled. Enter the product order number to finish the product return.",
        }));
        setBooking(null);
        setShop(null);
        setRefundInfo(null);
        setServiceTransactionContext(null);
        setBookingNumber("");
        setSubmitSuccess(false);
        setSubmitResult(null);
        return;
      }

      setTimeout(() => {
        setSubmitSuccess(false);
        setSubmitResult(null);
        setBooking(null);
        setShop(null);
        setRefundInfo(null);
        setServiceTransactionContext(null);
        setBookingNumber("");
      }, 8000);
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message;
      if (status === 429) {
        setError(msg || "Too many cancellation requests. Please contact the shop directly.");
      } else if (status === 403) {
        setError("The email or phone number did not match our records for this booking.");
      } else {
        setError(msg || "Failed to submit request. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * 22G — TAKE A GUEST TO THE PAYMENT THAT RESTORES THEIR SESSIONS.
   *
   * WHICH PROOF IT USES, AND WHY THAT WAS THE DEFECT (fixed 2026-10-16). This used to demand the package token
   * from `sessionStorage` and nothing else. A guest who arrives at this page and cancels has never run the package
   * challenge, so there was never a token to read: a blank tab opened, closed again, and the page said *"…you
   * will verify your email and phone again first"* — reported by the user after choosing **Pay now**.
   *
   * The server accepts EITHER a signed-in session or a scoped guest token, so this uses whichever it has, in that
   * order, and prefers the token the CANCELLATION ITSELF just returned: the cancellation proved the same email +
   * phone digits the package challenge does, which is why the server is willing to mint one. Only when there is
   * no proof at all does it say where to get one — and it no longer opens a tab it cannot use.
   *
   * Stripe opens in a NEW TAB, as the user asked: this page keeps its state, and the booking page picks the
   * returned sessions up on focus. The webhook returns them, so closing the tab is harmless.
   */
  const startGuestRedemption = async (
    bookingId,
    settledPurchaseId,
    reservedTab = null,
    cancellationToken = null,
    holdEntryIds = null,
  ) => {
    try {
      const sessionToken = localStorage.getItem("ogestyle_token");
      const guestToken =
        sessionStorage.getItem("ogestyle_guest_package_token");
      // THE CANCELLATION'S OWN TOKEN COMES FIRST. It is scoped to exactly this purchase and minted from the proof
      // this cancellation just made, so it works even when the caller is signed in as somebody else, or when the
      // package is an UNOWNED guest purchase that a session cannot redeem (the route matches a session against
      // `purchase.customerId`, which is null for a purchase made while logged out).
      const bearer = cancellationToken || sessionToken || guestToken;

      if (!bearer) {
        if (reservedTab) reservedTab.close();
        setError(
          "Your sessions are held and can be restored. To pay, reopen this booking on the booking page and use the “Pay to get them back” button there — you will verify your email and phone again first.",
        );
        return;
      }

      // The hold lives on the PURCHASE. It is named on the SETTLEMENT result the cancellation returned, and on the
      // warning the portal sent with the booking — either will do, and both are the SERVER's own answer rather
      // than a guess from the booking document (which the guest portal deliberately exposes very little of).
      const purchaseId = settledPurchaseId || packageCancellation?.purchaseId;
      if (!purchaseId) {
        if (reservedTab) reservedTab.close();
        setError("We could not find the package for this booking. Please contact the shop.");
        return;
      }

      const response = await axios.post(
        `${baseURL}/api/packages/purchase/${purchaseId}/redeem-held`,
        // THE HOLDS THIS CANCELLATION CREATED, so the charge matches the figure the customer was shown. Without
        // them the route prices every held session on the package: a cancellation quoted at $40 took them to a
        // payment for $80 (reported 2026-10-16).
        Array.isArray(holdEntryIds) && holdEntryIds.length > 0 ? { holdEntryIds } : {},
        { headers: { Authorization: `Bearer ${bearer}` } },
      );

      const url = String(response.data?.stripeCheckoutUrl || "");
      // Stripe's own domain or nothing: a tampered response must not become an open redirect.
      if (!/\.stripe\.com\//.test(url)) {
        if (reservedTab) reservedTab.close();
        setError("We could not open the payment page. Please try again from the booking page.");
        return;
      }

      if (!sendReservedTabToPayment(reservedTab, url)) {
        // OFFERED, never followed: navigating this tab is what lost the page they were on.
        setError(
          `Your browser blocked the payment window. Open the payment page from your booking page — the sessions stay held until you pay.`,
        );
      }
    } catch (err) {
      if (reservedTab) {
        try {
          reservedTab.close();
        } catch {
          // Nothing to do.
        }
      }
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        setError(
          "Your verification has expired, so the payment could not be opened. Reopen the booking on the booking page and verify again there — your sessions are still held for you.",
        );
        return;
      }
      setError(
        err.response?.data?.message ||
          "We could not open the payment page. Your sessions are still held — try again from the booking page.",
      );
    }
  };

  const handleLookupOrderForReturn = async (e) => {
    e.preventDefault();

    const normalizedEmail = productState.orderEmail.trim().toLowerCase();
    const normalizedOrderNumber = productState.orderNumber.trim().toUpperCase();

    if (!normalizedEmail || !normalizedOrderNumber) {
      updateProduct({ error: "Order email and order number are required." });
      return;
    }

    updateProduct({
      lookupLoading: true,
      error: "",
      successMessage: "",
      order: null,
      selectedItems: {},
      duplicateCase: null,
      lookupNotice: "",
      eligibility: null,
      lookupIneligibleMessage: "",
      transactionContext: null,
    });
    setProductMixedAction("");

    try {
      const response = await axios.get(
        `${baseURL}/api/orders/track?email=${encodeURIComponent(
          normalizedEmail,
        )}&orderNumber=${encodeURIComponent(
          normalizedOrderNumber,
        )}`,
      );

      const order = response.data?.order;
      if (!order) {
        updateProduct({ lookupLoading: false, error: "Order not found for those details." });
        return;
      }

      let existingCase = null;
      let duplicateMessage = "";
      try {
        const existingCaseResponse = await axios.post(
          `${baseURL}/api/product-returns/lookup-existing`,
          {
            email: normalizedEmail,
            orderNumber: normalizedOrderNumber,
          },
        );
        existingCase = existingCaseResponse.data?.existingCase || null;
        duplicateMessage = existingCaseResponse.data?.message || "";
        const relatedCases = Array.isArray(existingCaseResponse.data?.cases)
          ? existingCaseResponse.data.cases
          : [];
        const itemLocks = existingCaseResponse.data?.itemLocks || {};
        const lookupEligibility = existingCaseResponse.data?.eligibility || null;
        const ineligibleMessage =
          existingCaseResponse.data?.ineligibleMessage ||
          (lookupEligibility && !lookupEligibility.eligible
            ? "Your item is not eligible for return. Your return window has passed."
            : "");

        setProductState((prev) => ({
          ...prev,
          eligibility: lookupEligibility,
          lookupIneligibleMessage: ineligibleMessage,
          itemLocks,
          cases: relatedCases.length
            ? relatedCases.map(mapReturnCaseRow).filter(Boolean)
            : prev.cases,
        }));
      } catch (_existingCaseLookupError) {
        // Non-blocking: lookup should still complete even if duplicate pre-check fails.
      }

      const existingCaseRow = existingCase
        ? mapReturnCaseRow(existingCase)
        : null;
      const transactionContext = order.transactionContext || null;

      setProductState((prev) => ({
        ...prev,
        lookupLoading: false,
        order,
        transactionContext,
        duplicateCase: existingCase,
        lookupNotice: duplicateMessage,
        error: "",
        cases: existingCaseRow ? upsertReturnCase(prev.cases, existingCaseRow) : prev.cases,
      }));

      setProductMixedAction(transactionContext?.isMixedTransaction ? "" : "product_only");
    } catch (err) {
      updateProduct({
        lookupLoading: false,
        error: err.response?.data?.message || err.response?.data?.error || "Unable to fetch order details.",
      });
    }
  };

  const toggleProductItem = (orderItemId, isSelected) => {
    if (isItemSelectionLocked(orderItemId)) {
      return;
    }

    setProductState((prev) => {
      let next = { ...(prev.selectedItems || {}) };
      if (isSelected) {
        next = prev.submissionMode === "single" ? { [orderItemId]: true } : { ...next, [orderItemId]: true };
      }
      else delete next[orderItemId];

      const nextDetails = { ...(prev.itemDetailsById || {}) };
      if (isSelected && !nextDetails[orderItemId]) {
        nextDetails[orderItemId] = {
          reason: "",
          description: "",
          conditionAttestation: {
            itemUsedStatus: "unknown",
            packageOpenedStatus: "unknown",
            itemDamagedStatus: "unknown",
            reasonDetail: "",
          },
        };
      }

      return { ...prev, selectedItems: next, itemDetailsById: nextDetails };
    });
  };

  const setSubmissionMode = (mode) => {
    setProductState((prev) => {
      const nextMode = mode === "batch" ? "batch" : "single";
      let selectedItems = { ...(prev.selectedItems || {}) };
      let itemDetailsById = { ...(prev.itemDetailsById || {}) };
      if (nextMode === "single" && Object.keys(selectedItems).length > 1) {
        const [firstSelected] = Object.keys(selectedItems);
        selectedItems = firstSelected ? { [firstSelected]: true } : {};
        itemDetailsById = firstSelected && itemDetailsById[firstSelected]
          ? { [firstSelected]: itemDetailsById[firstSelected] }
          : {};
      }
      return {
        ...prev,
        submissionMode: nextMode,
        selectedItems,
        itemDetailsById,
      };
    });
  };

  const submitProductReturnCase = async (mixedTransactionConfirmed = false) => {
    if (!productState.order?.orderNumber) {
      updateProduct({ error: "Lookup your order first." });
      return;
    }

    if (productState.transactionContext?.isMixedTransaction && !mixedTransactionConfirmed && !productMixedAction) {
      updateProduct({ error: "Choose whether to return only the product or return the product and cancel the linked booking." });
      return;
    }

    if (productState.submissionMode === "single" && !productState.reason.trim()) {
      updateProduct({ error: "Please provide a return reason." });
      return;
    }

    if (!selectedReturnItems.length) {
      updateProduct({ error: "Select at least one product to return." });
      return;
    }

    if (productState.submissionMode === "batch") {
      const missingReasonItem = selectedReturnItems.find((item) => !String(item.reason || "").trim());
      if (missingReasonItem) {
        updateProduct({ error: "Each selected item in batch mode must include a return reason." });
        return;
      }
    }

    if (
      isDamagedOnlyPolicy &&
      selectedReturnItems.some(
        (item) => item?.conditionAttestation?.itemDamagedStatus !== "damaged",
      )
    ) {
      updateProduct({
        error:
          "This seller accepts returns for damaged/incorrect items only. In Item Condition Details, set 'Is the item damaged?' to 'Damaged' to continue.",
      });
      return;
    }

    if (productState.lookupIneligibleMessage) {
      updateProduct({ error: productState.lookupIneligibleMessage });
      return;
    }

    updateProduct({
      submitting: true,
      error: "",
      successMessage: "",
      duplicateCase: null,
      lookupNotice: "",
    });

    const payload = {
      orderNumber: productState.order.orderNumber,
      submissionMode: productState.submissionMode || "single",
      reason:
        productState.submissionMode === "batch"
          ? String(selectedReturnItems[0]?.reason || "").trim()
          : productState.reason.trim(),
      description:
        productState.submissionMode === "batch"
          ? String(selectedReturnItems[0]?.description || "").trim()
          : productState.description.trim(),
      items: selectedReturnItems,
      conditionAttestation: {
        itemUsedStatus: productState.conditionAttestation?.itemUsedStatus || "unknown",
        packageOpenedStatus:
          productState.conditionAttestation?.packageOpenedStatus || "unknown",
        itemDamagedStatus:
          productState.conditionAttestation?.itemDamagedStatus || "unknown",
        reasonDetail:
          productState.conditionAttestation?.reasonDetail?.trim() ||
          productState.description.trim(),
      },
    };

    try {
      let response;

      if (isSignedIn) {
        response = await axios.post(`${baseURL}/api/product-returns/cases`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        const guestPhoneLast4 = productState.phoneLast4.replace(/\D/g, "").slice(-4);
        if (!productState.orderEmail.trim() || guestPhoneLast4.length !== 4) {
          updateProduct({
            submitting: false,
            error: "Please enter the order email and last 4 digits of your phone number.",
          });
          return;
        }

        response = await submitGuestProductReturnDirect({
          ...payload,
          orderNumber: productState.order.orderNumber,
          email: productState.orderEmail,
          phoneLast4: guestPhoneLast4,
        });
      }

      const caseNumber = response.data?.case?.caseNumber || "";
      const responseCases = Array.isArray(response.data?.cases) ? response.data.cases : [];
      const batchReference = response.data?.batchReference || "";
      const returnWindowDays = Number(response.data?.case?.returnWindowDays || 7);
      const returnDueAt = response.data?.case?.returnDueAt
        ? formatReturnDeadline(response.data.case.returnDueAt)
        : "";
      const successMessage = batchReference && responseCases.length > 1
        ? `Batch return submitted. Reference: ${batchReference}. Cases created: ${responseCases.map((row) => row.caseNumber).filter(Boolean).join(", ")}. Please return each item within ${returnWindowDays} days${returnDueAt ? ` (first deadline by ${returnDueAt})` : ""}.`
        : caseNumber
          ? `Return request submitted. Case number: ${caseNumber}. Please return the item within ${returnWindowDays} days${returnDueAt ? ` (by ${returnDueAt})` : ""}.`
          : "Return request submitted.";

      resetProductFlow(successMessage);

      setTimeout(() => {
        setProductState(createInitialProductState());
      }, 8000);

      return;
    } catch (err) {
      const duplicateCase = err.response?.data?.existingCase || null;
      const duplicateCaseRow = duplicateCase
        ? mapReturnCaseRow(duplicateCase)
        : null;

      setProductState((prev) => ({
        ...prev,
        submitting: false,
        error: err.response?.data?.message || err.message || "Unable to submit return request.",
        duplicateCase,
        lookupNotice: "",
        cases: duplicateCaseRow ? upsertReturnCase(prev.cases, duplicateCaseRow) : prev.cases,
      }));
    }
  };

  const loadReturnCases = async () => {
    updateProduct({ casesLoading: true, error: "" });

    try {
      let response;
      if (isSignedIn) {
        response = await axios.get(`${baseURL}/api/product-returns/cases`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        const guestPhoneLast4 = productState.phoneLast4.replace(/\D/g, "").slice(-4);
        if (!productState.order?.orderNumber || !productState.orderEmail.trim() || guestPhoneLast4.length !== 4) {
          updateProduct({
            casesLoading: false,
            error: "Lookup your order and enter the last 4 digits of your phone number first.",
          });
          return;
        }

        response = await axios.get(`${baseURL}/api/product-returns/guest/cases/direct`, {
          params: {
            orderNumber: productState.order.orderNumber,
            email: productState.orderEmail.trim().toLowerCase(),
            phoneLast4: guestPhoneLast4,
          },
        });
      }

      updateProduct({ casesLoading: false, cases: response.data?.cases || [] });
    } catch (err) {
      updateProduct({
        casesLoading: false,
        error: err.response?.data?.message || "Unable to load return cases.",
      });
    }
  };

  const handleViewExistingCase = async () => {
    if (!productState.duplicateCase) {
      return;
    }

    await loadReturnCases();
  };

  const hasEstimatedRefundAmount = Number(refundInfo?.estimatedRefund || 0) > 0;

  /**
   * The refund estimate must follow WHICH bookings are ticked.
   *
   * `refundInfo.estimatedRefund` is the server's figure for the looked-up booking
   * ALONE, so once the customer ticks extra group members it no longer describes what
   * they are about to submit. For a multi-booking selection this shows the selected
   * service value with the SAME fee percentage the server applied, and leaves the
   * non-refundable processing fee to the note already shown underneath.
   *
   * A selection of one (or a booking that is not in a group) keeps the server's own
   * number, unchanged.
   */
  const selectedGroupMembers = groupMembers.filter((member) =>
    cancelSelection.includes(member._id),
  );
  const showsSelectionEstimate =
    groupMembers.length > 1 && selectedGroupMembers.length > 1;
  const selectionServiceTotal = selectedGroupMembers.reduce(
    (sum, member) => sum + (Number(member.servicePrice) || 0),
    0,
  );
  const appliedFeePercent = Number(refundInfo?.appliedFeePercent) || 0;
  const displayedEstimatedRefund = showsSelectionEstimate
    ? selectionServiceTotal * (1 - appliedFeePercent / 100)
    : Number(refundInfo?.estimatedRefund || 0);

  /**
   * ── WHAT THIS APPOINTMENT COST, FOR THE WHOLE GROUP (user, 2026-10-17) ──────────────────────────
   *
   * *"the amount highlighted there is wrong for both card and package methods when the booking is for group
   * bookings, it only picks the figure for the primary member alone"*. The Booking Details panel printed
   * `booking.totalCost` — the ONE booking the guest looked up — so a two-person appointment showed $30 instead of
   * the $50 the group cost. This is the group's total whenever the lookup returned a group, and a PACKAGE row is
   * priced at its package value (`basis`), the same rule the rows below use.
   */
  const groupDisplayTotal = (() => {
    if (groupMembers.length <= 1) return Number(booking?.totalCost) || 0;
    return Math.round(
      groupMembers.reduce((sum, member) => {
        const pkgRow = packageCancellations[String(member._id)];
        return sum + (pkgRow ? Number(pkgRow.basis) || 0 : Number(member.servicePrice) || 0);
      }, 0) * 100,
    ) / 100;
  })();

  /**
   * 22G — THE PACKAGE SIDE OF WHICHEVER BOOKINGS ARE TICKED, totalled from the SERVER's per-booking figures.
   *
   * Kept separate from the card estimate above ON PURPOSE: sessions lost from the package and money returned to
   * the card are different things, and a group can be one of each. Only the ticked rows are added; no tier is
   * evaluated and no frozen price is read here.
   */
  const packagePreview = (() => {
    const rows = (selectedGroupMembers.length > 0
      ? selectedGroupMembers
      : booking
        ? [{ _id: String(booking._id) }]
        : []
    )
      .map((member) => packageCancellations[String(member._id)])
      .filter(Boolean);

    if (rows.length === 0) return null;
    const currencies = [...new Set(rows.map((row) => row.currency))];
    const sum = (field) =>
      Math.round(rows.reduce((acc, row) => acc + (Number(row[field]) || 0), 0) * 100) / 100;

    return {
      bookings: rows.length,
      units: rows.reduce((acc, row) => acc + (Number(row.units) || 0), 0),
      // "in time" only when EVERY ticked booking is: one late cancellation is what makes something lost.
      inTime: rows.every((row) => row.inTime),
      // The band when every ticked booking shares it, and the VALUE of the sessions — without these the fee line
      // reads "Value $0.00". Every amount is still the server's own.
      feePercent: rows.every((row) => row.feePercent === rows[0].feePercent)
        ? rows[0].feePercent
        : null,
      basis: sum("basis"),
      redeemAmount: sum("redeemAmount"),
      // THE SINGLE RULE (2026-10-17): the refund is offered iff this is above zero — there is no separate
      // eligibility flag to disagree with the number. The fee is carried so the deduction is always shown.
      refundAmount: sum("refundAmount"),
      processingFee: sum("processingFee"),
      netBasis: sum("netBasis"),
      currency: currencies.length === 1 ? currencies[0] : null,
      purchaseId: rows[0].purchaseId,
    };
  })();

  return (
    <>
      <CustomerHeader />

      <div
        style={{
          maxWidth: "860px",
          margin: "0 auto",
          padding: "40px 20px",
          paddingTop: "120px",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <h1 style={{ fontSize: "30px", fontWeight: 700, color: "#1f2937", margin: 0 }}>
            Service Cancellations and Product Returns
          </h1>
        </div>

        <div style={{ display: "flex", gap: "10px", marginBottom: "22px", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => setMode("service")}
            style={{
              padding: "10px 14px",
              borderRadius: "8px",
              border: mode === "service" ? "1px solid #2563eb" : "1px solid #d1d5db",
              background: mode === "service" ? "#eff6ff" : "#ffffff",
              color: mode === "service" ? "#1d4ed8" : "#374151",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Service Cancellation
          </button>

          <button
            type="button"
            onClick={() => setMode("product")}
            style={{
              padding: "10px 14px",
              borderRadius: "8px",
              border: mode === "product" ? "1px solid #16a34a" : "1px solid #d1d5db",
              background: mode === "product" ? "#f0fdf4" : "#ffffff",
              color: mode === "product" ? "#166534" : "#374151",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Product Return
          </button>
        </div>

        {mode === "service" && (
          <>
            <form onSubmit={handleLookup} style={{ display: submitSuccess || booking ? "none" : "block", marginBottom: "32px" }}>
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", fontSize: "14px", fontWeight: "600", color: "#1f2937", marginBottom: "8px" }}>
                  Booking Email
                </label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="you@example.com"
                  style={{ width: "100%", padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" }}
                  disabled={loading}
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", fontSize: "14px", fontWeight: "600", color: "#1f2937", marginBottom: "8px" }}>
                  Booking Number
                </label>
                <input
                  type="text"
                  value={bookingNumber}
                  onChange={(e) => setBookingNumber(e.target.value)}
                  placeholder="e.g., BKG-1721756893452-742"
                  style={{ width: "100%", padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box", fontFamily: "monospace" }}
                  disabled={loading}
                />
              </div>

              {error && <div style={{ padding: "12px", background: "#fee2e2", border: "1px solid #fecaca", borderRadius: "8px", color: "#991b1b", fontSize: "14px", marginBottom: "20px" }}>❌ {error}</div>}

              <button type="submit" disabled={loading} style={{ width: "100%", padding: "12px", background: loading ? "#9ca3af" : "#2563eb", color: "white", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: loading ? "not-allowed" : "pointer" }}>
                {loading ? "Looking up..." : "Lookup Booking"}
              </button>
            </form>

            {submitSuccess && (
              <div style={{ padding: "24px", background: submitResult?.queued ? "#fffbeb" : "#f0fdf4", border: submitResult?.queued ? "1px solid #fde68a" : "1px solid #bbf7d0", borderRadius: "8px", textAlign: "center", marginBottom: "32px" }}>
                <p style={{ fontSize: "16px", fontWeight: "600", color: submitResult?.queued ? "#92400e" : "#15803d", margin: "0 0 8px 0" }}>
                  {submitResult?.queued ? "⏳ Request Under Review" : "✅ Booking Cancelled"}
                </p>
                <p style={{ fontSize: "14px", color: submitResult?.queued ? "#78350f" : "#166534", margin: 0 }}>
                  {submitResult?.message || "Your request has been submitted."}
                </p>
              </div>
            )}

            {booking && !submitSuccess && (
              <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "20px", marginBottom: "24px" }}>
                <h2 style={{ fontSize: "16px", fontWeight: "600", color: "#1f2937", margin: "0 0 16px 0" }}>Booking Details</h2>
                <div style={{ marginBottom: "12px" }}><p style={{ fontSize: "12px", color: "#6b7280", margin: "0 0 4px 0" }}>Booking Number</p><p style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937", margin: 0, fontFamily: "monospace" }}>{booking.bookingNumber}</p></div>
                <div style={{ marginBottom: "12px" }}><p style={{ fontSize: "12px", color: "#6b7280", margin: "0 0 4px 0" }}>Date & Time</p><p style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937", margin: 0 }}>{formatBookingCalendarDate(booking.appointmentDate)} at {booking.startTime}</p></div>
                <div style={{ marginBottom: "12px" }}><p style={{ fontSize: "12px", color: "#6b7280", margin: "0 0 4px 0" }}>Total Cost{groupMembers.length > 1 ? ` (${groupMembers.length} bookings)` : ""}</p><p style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937", margin: 0 }}>${groupDisplayTotal.toFixed(2)}</p></div>
                <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "12px" }}><p style={{ fontSize: "12px", color: "#6b7280", margin: "0 0 4px 0" }}>Status</p><div style={{ display: "inline-block", padding: "4px 8px", background: booking.status === "confirmed" ? "#dbeafe" : "#f3f4f6", color: booking.status === "confirmed" ? "#0c4a6e" : "#374151", borderRadius: "4px", fontSize: "12px", fontWeight: "600" }}>{booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}</div></div>
              </div>
            )}

            {refundInfo && !submitSuccess && (
              <div style={{ background: refundInfo.canRequestRefund && booking.status === "confirmed" ? "#f0fdf4" : "#fef3c7", border: refundInfo.canRequestRefund && booking.status === "confirmed" ? "1px solid #bbf7d0" : "1px solid #fde68a", borderRadius: "8px", padding: "20px", marginBottom: "24px" }}>
                <h2 style={{ fontSize: "16px", fontWeight: "600", color: refundInfo.canRequestRefund && booking.status === "confirmed" ? "#166534" : "#92400e", margin: "0 0 12px 0" }}>
                  {/*
                    22G — A PACKAGE BOOKING IS NOT A CARD REFUND (user, 2026-10-14).
                    This panel used to announce "Refund Eligible" and a dollar figure on a booking that was paid
                    with a package — money that was never charged to a card for this appointment. The package
                    panel below says what actually happens, so this one says what it is: nothing moves on the card.
                  */}
                  {packagePreview
                    ? "💳 Paid with your package"
                    : refundInfo.canRequestRefund && booking.status === "confirmed"
                      ? "✅ Refund Eligible"
                      : "⚠️ Refund Not Available"}
                </h2>
                <p style={{ fontSize: "14px", color: refundInfo.canRequestRefund && booking.status === "confirmed" ? "#166534" : "#92400e", margin: "0 0 12px 0" }}>
                  {packagePreview
                    ? "This appointment was paid for with your package, so a card refund is not calculated for it here. What happens to the sessions — and what comes back to the card if you let them go — is set out just below."
                    : refundInfo.reason}
                </p>
                {!packagePreview && refundInfo.canRequestRefund && booking.status === "confirmed" && (
                  <div style={{ background: "white", padding: "12px", borderRadius: "4px", marginBottom: "12px" }}>
                    <p style={{ fontSize: "12px", color: "#6b7280", margin: "0 0 4px 0" }}>
                      {showsSelectionEstimate
                        ? `Estimated Refund for ${selectedGroupMembers.length} selected bookings (based on cancellation policy)`
                        : "Estimated Refund (based on cancellation policy)"}
                    </p>
                    <p style={{ fontSize: "18px", fontWeight: "bold", color: Number(displayedEstimatedRefund) > 0 ? "#15803d" : "#991b1b", margin: 0 }}>${Number(displayedEstimatedRefund).toFixed(2)}</p>
                    <p style={{ fontSize: "11px", color: "#6b7280", margin: "4px 0 0 0" }}>
                      {refundInfo.hoursUntilAppointment > 0 ? `${Math.round(refundInfo.hoursUntilAppointment)} hours until appointment` : "Appointment has already started"}
                      {appliedFeePercent > 0 && <span style={{ color: "#b45309", marginLeft: "6px" }}>· {appliedFeePercent}% cancellation fee applies</span>}
                      {showsSelectionEstimate && groupMembers.length > selectedGroupMembers.length && (
                        <span style={{ marginLeft: "6px" }}>· {groupMembers.length - selectedGroupMembers.length} of {groupMembers.length} bookings stay</span>
                      )}
                    </p>
                    <p style={{ fontSize: "11px", color: "#9a3412", margin: "8px 0 0 0", lineHeight: "1.5" }}>{NON_REFUNDABLE_PROCESSING_FEE_REFUND_NOTE}</p>
                  </div>
                )}
                {/* The processing-fee note is about CARD money; on a package booking it describes a charge that
                    was never made for this appointment. */}
                {!packagePreview && (
                  <p style={{ fontSize: "12px", color: "#7c2d12", lineHeight: "1.5", marginTop: "12px" }}>{NON_REFUNDABLE_PROCESSING_FEE_NOTICE}</p>
                )}
              </div>
            )}

            {/* ── 22G: WHAT CANCELLING COSTS IN PACKAGE SESSIONS ────────────────────────────────────────

                A GUEST has no account and no dashboard — this page is the only place they can be warned before
                they cancel, so the warning and the choice both live here. The numbers come from the server (the
                same function that prices the hold when the cancellation happens), so the amount shown is the
                amount charged. */}
            {booking && packagePreview && !submitSuccess && (
              <div
                style={{
                  background: "#fffbeb",
                  border: "1px solid #fcd34d",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "24px",
                }}
              >
                <h2 style={{ fontSize: "15px", fontWeight: "700", color: "#92400e", margin: "0 0 8px 0" }}>
                  ⚠️ {packagePreview.bookings} of these booking
                  {packagePreview.bookings === 1 ? "" : "s"} used your package
                </h2>
                <p style={{ fontSize: "13px", color: "#78350f", margin: "0 0 12px 0", lineHeight: 1.5 }}>
                  {packagePreview.inTime
                    ? `Cancelling now returns ${packagePreview.units} session${
                        packagePreview.units === 1 ? "" : "s"
                      } to your package and nothing is charged. You can keep them on the package, or let them go and take a refund.`
                    : `Cancelling now loses ${packagePreview.units} session${
                        packagePreview.units === 1 ? "" : "s"
                      } from your package. You can keep them by paying the late-cancellation charge${
                        Number(packagePreview.refundAmount) > 0
                          ? ", or take a refund for the part that is refundable."
                          : ", or let them go — nothing is left to refund at this notice."
                      }`}
                </p>

                {/* THE CHOICE IS OFFERED AT EVERY BAND (user, 2026-10-17): *"when the cancellation charge is
                    0%, the losse or keep option is not displayed, neither is refund option shown, can it be
                    uniform. The only time there's no refund option is when its 100% charge."* At 0% KEEP costs
                    nothing and LOSE refunds the whole net; only the wording changes. */}
                <>
                    {/* THE LOSE CHOICE IS ALWAYS OFFERED (user, 2026-10-17). This used to be gated on
                        `refundEligible`, so at a 100% notice a guest saw keep-only and could not decline. Their
                        words: *“The keep or loose is one sided, meaning they only have one choice and that is
                        to keep.”* Only the wording changes when there is nothing to refund. */}
                    <label
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "flex-start",
                        fontSize: "13px",
                        color: "#78350f",
                        padding: "8px",
                        borderRadius: "6px",
                        background: packageChoice === "refund" ? "#fef3c7" : "transparent",
                        cursor: "pointer",
                        lineHeight: 1.5,
                      }}
                    >
                      <input
                        type="radio"
                        name="guestPackageChoice"
                        checked={packageChoice === "refund"}
                        onChange={() => setPackageChoice("refund")}
                        style={{ marginTop: "3px" }}
                      />
                      <span>
                        {Number(packagePreview.refundAmount) > 0 ? (
                          <>
                            <strong>
                              Take the refund — ${Number(packagePreview.refundAmount).toFixed(2)}
                            </strong>
                            <br />
                            The sessions are lost and this much goes back to the card.
                            {/* SEE THE DASHBOARD: the deduction is named so the net refund is explained. */}
                            {Number(packagePreview.processingFee) > 0 && (
                              <>
                                <br />
                                <span style={{ color: "#92400e" }}>
                                  Value ${Number(packagePreview.basis || 0).toFixed(2)} − card
                                  processing fee $
                                  {Number(packagePreview.processingFee).toFixed(2)}
                                  {Number(packagePreview.feePercent) > 0 &&
                                    Number(packagePreview.redeemAmount) > 0 &&
                                    ` − late-cancellation share $${Number(
                                      packagePreview.redeemAmount,
                                    ).toFixed(2)}`}
                                </span>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            <strong>Let the sessions go — nothing refunded</strong>
                            <br />
                            Nothing is left to refund at this notice, so the sessions are simply
                            given up.
                          </>
                        )}
                      </span>
                    </label>

                    <label
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "flex-start",
                        fontSize: "13px",
                        color: "#78350f",
                        padding: "8px",
                        borderRadius: "6px",
                        background: packageChoice === "redeem" ? "#fef3c7" : "transparent",
                        cursor: "pointer",
                        lineHeight: 1.5,
                      }}
                    >
                      <input
                        type="radio"
                        name="guestPackageChoice"
                        checked={packageChoice === "redeem"}
                        onChange={() => setPackageChoice("redeem")}
                        style={{ marginTop: "3px" }}
                      />
                      <span>
                        {packagePreview.inTime ? (
                          <>
                            <strong>Keep the sessions — back on your package</strong>
                            <br />
                            Nothing is charged inside the free cancellation window.
                          </>
                        ) : (
                          <>
                            <strong>
                              Keep the sessions — pay ${packagePreview.redeemAmount.toFixed(2)}
                            </strong>
                            <br />
                            Pay now, or later when you book with this package. They go back on the package
                            once it is paid.
                          </>
                        )}
                      </span>
                    </label>

                    {/* PAY NOW OR PAY LATER (user, 2026-10-14). Only on the keep-the-sessions side, and only
                        when there is something to pay: at 0% the sessions simply go back. */}
                    {!packagePreview.inTime && (
                    <div
                      style={{
                        marginTop: "8px",
                        paddingTop: "8px",
                        borderTop: "1px dashed #fcd34d",
                        fontSize: "12px",
                        color: "#78350f",
                      }}
                    >
                      <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                        When would you like to pay?
                      </div>
                      <label style={{ display: "flex", gap: "8px", alignItems: "center", padding: "4px 0", cursor: "pointer" }}>
                        <input
                          type="radio"
                          name="guestPackagePayTiming"
                          checked={packagePayTiming === "now"}
                          onChange={() => setPackagePayTiming("now")}
                        />
                        <span>
                          <strong>Pay now</strong> — a payment window opens as the cancellation completes.
                        </span>
                      </label>
                      <label style={{ display: "flex", gap: "8px", alignItems: "center", padding: "4px 0", cursor: "pointer" }}>
                        <input
                          type="radio"
                          name="guestPackagePayTiming"
                          checked={packagePayTiming === "later"}
                          onChange={() => setPackagePayTiming("later")}
                        />
                        <span>
                          <strong>Pay later</strong> — the offer waits on your booking page, and your other
                          sessions keep working.
                        </span>
                      </label>
                    </div>
                    )}

                    {/* ── PAY FOR THIS CANCELLATION, OR FOR EVERYTHING HELD (user, 2026-10-16) ──
                        *"include the system searching if there are other cancelled session(s) associated with
                        the package number and give the customer/guest the option of paying altogether"*. Shown
                        only when the package already holds sessions from an EARLIER cancellation, and only for
                        "pay now" — paying later opens nothing, so there is nothing to scope. Both figures come
                        from the server, and the ids that were shown are the ids that get priced. */}
                    {(packageCancellation?.otherHeldUnits || 0) > 0 &&
                      !packagePreview.inTime &&
                      packagePayTiming === "now" && (
                      <div
                        style={{
                          marginTop: "10px",
                          paddingTop: "10px",
                          borderTop: "1px dashed #fcd34d",
                        }}
                      >
                        <div style={{ fontWeight: "600", marginBottom: "6px" }}>
                          What would you like to pay for?
                        </div>
                        <label
                          style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "flex-start",
                            padding: "4px 0",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="radio"
                            name="guestPackagePayScope"
                            checked={packagePayScope === "this"}
                            onChange={() => setPackagePayScope("this")}
                            style={{ marginTop: "2px" }}
                          />
                          <span>
                            <strong>
                              Just this cancellation — $
                              {Number(packagePreview?.redeemAmount || 0).toFixed(2)}
                            </strong>
                            <br />
                            {Number(packagePreview?.units || 0)} session(s). Your other held sessions stay as
                            they are.
                          </span>
                        </label>
                        <label
                          style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "flex-start",
                            padding: "4px 0",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="radio"
                            name="guestPackagePayScope"
                            checked={packagePayScope === "all"}
                            onChange={() => setPackagePayScope("all")}
                            style={{ marginTop: "2px" }}
                          />
                          <span>
                            <strong>
                              Everything held on this package — $
                              {(
                                Number(packagePreview?.redeemAmount || 0) +
                                Number(packageCancellation?.otherHeldAmount || 0)
                              ).toFixed(2)}
                            </strong>
                            <br />
                            {Number(packagePreview?.units || 0) +
                              Number(packageCancellation?.otherHeldUnits || 0)}{" "}
                            session(s) in one payment — including the{" "}
                            {packageCancellation?.otherHeldUnits} held from an earlier cancellation.
                          </span>
                        </label>
                      </div>
                    )}
                  </>
              </div>
            )}

            {/* About CARD money, so it is not shown on a booking that was paid with a package — the package panel
                above already says exactly what happens. */}
            {booking && refundInfo && !submitSuccess && !packagePreview && refundInfo.canRequestRefund && !hasEstimatedRefundAmount && (
              <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: "8px", padding: "14px", marginBottom: "24px" }}>
                <p style={{ margin: 0, fontSize: "13px", color: "#9a3412", fontWeight: "600" }}>
                  There is no refundable amount available for this booking based on the cancellation policy. You can still cancel the booking, but no refund will be issued.
                </p>
              </div>
            )}

            {booking && refundInfo && !submitSuccess && !refundInfo.canRequestRefund && (
              <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: "8px", padding: "14px", marginBottom: "24px" }}>
                <p style={{ margin: 0, fontSize: "13px", color: "#9a3412", fontWeight: "600" }}>
                  {refundInfo.reason || "This booking cannot be cancelled online."}
                </p>
                <div style={{ marginTop: "10px" }}>
                  <button type="button" onClick={resetServiceFlow} style={{ padding: "8px 12px", background: "white", color: "#6b7280", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>
                    Start Over
                  </button>
                </div>
              </div>
            )}

            {booking && serviceTransactionContext?.isMixedTransaction && !submitSuccess && (
              <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "8px", padding: "16px", marginBottom: "16px" }}>
                <div style={{ fontSize: "15px", fontWeight: 700, color: "#1e3a8a", marginBottom: "8px" }}>This booking is part of a mixed order.</div>
                <div style={{ fontSize: "13px", color: "#334155", lineHeight: "1.5", marginBottom: "10px" }}>
                  Linked order: <strong>{serviceTransactionContext.linkedOrderNumber || "Product order"}</strong>
                </div>
                {serviceTransactionContext.productSummary?.length > 0 && (
                  <div style={{ background: "white", border: "1px solid #dbeafe", borderRadius: "8px", padding: "10px", marginBottom: "12px" }}>
                    {serviceTransactionContext.productSummary.slice(0, 4).map((item, index) => (
                      <div key={`${item.orderItemId || item.name}-${index}`} style={{ fontSize: "12px", color: "#334155", marginBottom: index === serviceTransactionContext.productSummary.slice(0, 4).length - 1 ? 0 : "4px" }}>
                        {item.quantity || 1}x {item.name || "Product"}
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: "13px", color: "#334155", marginBottom: "10px" }}>What do you want to cancel?</div>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <button type="button" onClick={() => setServiceMixedAction("booking_only")} style={{ padding: "10px 14px", borderRadius: "8px", border: serviceMixedAction === "booking_only" ? "1px solid #0c4a6e" : "1px solid #cbd5e1", background: serviceMixedAction === "booking_only" ? "#0c4a6e" : "white", color: serviceMixedAction === "booking_only" ? "white" : "#334155", fontWeight: 600, cursor: "pointer" }}>
                    Cancel Booking Only
                  </button>
                  <button type="button" onClick={() => setServiceMixedAction("both")} style={{ padding: "10px 14px", borderRadius: "8px", border: serviceMixedAction === "both" ? "1px solid #15803d" : "1px solid #86efac", background: serviceMixedAction === "both" ? "#15803d" : "white", color: serviceMixedAction === "both" ? "white" : "#166534", fontWeight: 600, cursor: "pointer" }}>
                    Cancel Booking + Return Products
                  </button>
                </div>
              </div>
            )}

            {showRequestForm && booking && !submitSuccess && refundInfo?.canRequestRefund && (
              <form id="service-cancellation-form" onSubmit={(e) => {
                if (serviceTransactionContext?.isMixedTransaction && serviceMixedAction === "both") {
                  e.preventDefault();
                  submitServiceAndProductTogether();
                  return;
                }
                handleSubmitRequest(e, !serviceTransactionContext?.isMixedTransaction || serviceMixedAction === "booking_only");
              }} style={{ background: "#f0f9ff", border: "1px solid #bfdbfe", borderRadius: "8px", padding: "20px" }}>
                {error && <div style={{ padding: "12px", background: "#fee2e2", border: "1px solid #fecaca", borderRadius: "8px", color: "#991b1b", fontSize: "14px", marginBottom: "16px" }}>❌ {error}</div>}

                {groupMembers.length > 1 && (
                  <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", marginBottom: "14px", overflow: "hidden" }}>
                    <div style={{ padding: "10px 12px", background: "#f9fafb", fontSize: "13px", fontWeight: 600 }}>
                      Who is this cancellation for?
                    </div>
                    {groupMembers.map((member) => {
                      const allTicked = cancelSelection.length === groupMembers.length;
                      const ticked = cancelSelection.includes(member._id);
                      // Ticking the anchor ticks everything, so a member is locked once
                      // the whole group is selected.
                      const locked = !member.isPrimary && allTicked;
                      // A PACKAGE BOOKING IS WORTH WHAT WAS PAID FOR ITS SESSIONS, NOT THE LIST PRICE (user,
                      // 2026-10-17): these rows printed `servicePrice` — the shop's price — so an $80 package
                      // showed $30 and $20 for sessions worth $24 and $16. The server prices each booking's own
                      // loss in `packageCancellations`, whose `basis` is the PAID value of the units it spent.
                      const pkgRow = packageCancellations[String(member._id)];
                      const shownPrice = pkgRow
                        ? Number(pkgRow.basis) || 0
                        : Number(member.servicePrice) || 0;
                      return (
                        <label
                          key={member._id}
                          style={{ display: "flex", gap: "10px", padding: "10px 12px", borderTop: "1px solid #f3f4f6", cursor: locked ? "default" : "pointer", alignItems: "flex-start" }}
                        >
                          <input
                            type="checkbox"
                            checked={ticked}
                            disabled={locked || submitting}
                            onChange={() => handleCancelTargetToggle(member)}
                            style={{ marginTop: "3px" }}
                          />
                          <span style={{ fontSize: "13px", lineHeight: 1.5, color: "#334155" }}>
                            <span style={{ fontWeight: 600 }}>{member.label}</span>
                            {" — "}
                            {member.serviceName || "Service"}
                            {member.startTime ? ` at ${formatClockTime(member.startTime)}` : ""}
                            {shownPrice ? ` · $${shownPrice.toFixed(2)}${pkgRow ? " package value" : ""}` : ""}
                            {member.isPrimary && (
                              <span style={{ display: "block", color: "#991b1b", marginTop: "2px" }}>
                                ⚠️ This is the anchor booking. Cancelling it cancels all {groupMembers.length} bookings.
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                    {cancelSelection.length === 0 && (
                      <div style={{ padding: "8px 12px", fontSize: "12px", color: "#991b1b", borderTop: "1px solid #f3f4f6" }}>
                        Select at least one booking to continue.
                      </div>
                    )}
                    <div style={{ padding: "8px 12px", fontSize: "11px", color: "#6b7280", borderTop: "1px solid #f3f4f6" }}>
                      This appointment was paid as one payment, so the refund is issued once for whichever bookings you select.
                    </div>
                  </div>
                )}

                <label htmlFor="cancel-reason-category" style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#374151", marginBottom: "6px" }}>
                  Why are you cancelling?
                </label>
                <select
                  id="cancel-reason-category"
                  value={cancelReasonCategory}
                  onChange={(e) => setCancelReasonCategory(e.target.value)}
                  disabled={submitting}
                  style={{ width: "100%", padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box", marginBottom: "12px" }}
                >
                  <option value="customer_requested">I changed my mind / no longer need it</option>
                  <option value="customer_unavailable">I can no longer attend</option>
                </select>
                <input type="text" value={phoneLast4} onChange={(e) => setPhoneLast4(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="Last 4 digits of your phone" maxLength={4} inputMode="numeric" style={{ width: "180px", padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box", marginBottom: "12px" }} disabled={submitting} />
                <button type="submit" disabled={submitting || (serviceTransactionContext?.isMixedTransaction && !serviceMixedAction) || (groupMembers.length > 1 && cancelSelection.length === 0) || (packagePreview && !packageChoice)} style={{ width: "100%", padding: "12px", background: submitting || (serviceTransactionContext?.isMixedTransaction && !serviceMixedAction) || (groupMembers.length > 1 && cancelSelection.length === 0) || (packagePreview && !packageChoice) ? "#9ca3af" : "#0c4a6e", color: "white", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: submitting || (serviceTransactionContext?.isMixedTransaction && !serviceMixedAction) || (groupMembers.length > 1 && cancelSelection.length === 0) || (packagePreview && !packageChoice) ? "not-allowed" : "pointer" }}>{submitting ? "Processing..." : packagePreview && !packageChoice ? "Choose above: keep or lose your sessions" : serviceMixedAction === "both" ? "Cancel Booking + Return Products" : "Cancel Booking"}</button>
              </form>
            )}
          </>
        )}

        {mode === "product" && (
          <>
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", padding: "14px", marginBottom: "18px" }}>
              <div style={{ fontSize: "14px", fontWeight: "700", color: "#166534", marginBottom: "8px" }}>How to use Product Return</div>
              <div style={{ fontSize: "13px", color: "#166534", lineHeight: "1.6" }}>1. Enter order email and order number, then lookup.</div>
              <div style={{ fontSize: "13px", color: "#166534", lineHeight: "1.6" }}>2. If the order includes a booking, choose whether to handle only the product or both together.</div>
              <div style={{ fontSize: "13px", color: "#166534", lineHeight: "1.6" }}>3. Select items, enter phone last 4 if prompted, and submit.</div>
              <div style={{ fontSize: "13px", color: "#166534", lineHeight: "1.6", marginTop: "4px", fontWeight: 600 }}>
                Return policy: physically return the product within 7 days after request submission.
              </div>
            </div>

            <form onSubmit={handleLookupOrderForReturn} style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "6px", fontWeight: 600 }}>Order Email</label>
              <input type="email" value={productState.orderEmail} onChange={(e) => updateProduct({ orderEmail: e.target.value })} style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "10px", boxSizing: "border-box", marginBottom: "10px" }} />
              <label style={{ display: "block", marginBottom: "6px", fontWeight: 600 }}>Order Number</label>
              <input type="text" value={productState.orderNumber} onChange={(e) => updateProduct({ orderNumber: e.target.value.toUpperCase() })} style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "10px", boxSizing: "border-box", marginBottom: "10px" }} />
              <button type="submit" disabled={productState.lookupLoading} style={{ padding: "10px 16px", background: "#16a34a", color: "#fff", border: "none", borderRadius: "8px", fontWeight: 600, cursor: "pointer" }}>{productState.lookupLoading ? "Looking up..." : "Lookup Order"}</button>
            </form>

            {productState.order && !productState.lookupIneligibleMessage && (
              <div style={{ border: "1px solid #dcfce7", borderRadius: "8px", padding: "14px", background: "#f9fffb", marginBottom: "14px" }}>
                <div style={{ fontWeight: 700, marginBottom: "8px" }}>Order Found</div>
                <div style={{ fontSize: "14px", marginBottom: "8px" }}>Order: {productState.order.orderNumber}</div>
                {productState.transactionContext?.isMixedTransaction && productState.transactionContext?.linkedBookings?.length > 0 && (
                  <div style={{ border: "1px solid #bfdbfe", borderRadius: "8px", background: "#eff6ff", color: "#1e3a8a", padding: "10px", marginBottom: "12px", fontSize: "13px", lineHeight: "1.45" }}>
                    <div style={{ fontWeight: 700, marginBottom: "4px" }}>This product order is part of a mixed order.</div>
                    <div>
                      Linked booking: {productState.transactionContext.linkedBookings.map((row) => row.bookingNumber || row.serviceName || "Service").filter(Boolean).join(", ")}
                    </div>
                    {productState.transactionContext.linkedBookings.map((row, index) => {
                      const eligibility = row.cancellationEligibility || null;
                      if (!eligibility) return null;
                      return (
                        <div key={`${row.bookingId || row.bookingNumber || index}-eligibility`} style={{ background: "white", border: "1px solid #dbeafe", borderRadius: "8px", padding: "8px", marginTop: "8px", color: eligibility.canRequestRefund ? "#166534" : "#991b1b" }}>
                          <div style={{ fontWeight: 700 }}>{eligibility.canRequestRefund ? "Booking cancellation available" : "Booking cancellation not available"}</div>
                          <div>{eligibility.reason}</div>
                          {eligibility.canRequestRefund && (
                            <div>Estimated booking refund: <strong>${Number(eligibility.estimatedRefund || 0).toFixed(2)}</strong></div>
                          )}
                        </div>
                      );
                    })}
                    <div style={{ color: "#475569", marginTop: "4px" }}>
                      Choose whether you want to return only the product or return the product and cancel the linked booking before continuing.
                    </div>
                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "10px" }}>
                      <button type="button" onClick={() => setProductMixedAction("product_only")} style={{ padding: "9px 12px", borderRadius: "8px", border: productMixedAction === "product_only" ? "1px solid #15803d" : "1px solid #86efac", background: productMixedAction === "product_only" ? "#15803d" : "white", color: productMixedAction === "product_only" ? "white" : "#166534", fontWeight: 600, cursor: "pointer" }}>
                        Return Product Only
                      </button>
                      <button type="button" disabled={productState.transactionContext.linkedBookings.some((row) => row.cancellationEligibility && !row.cancellationEligibility.canRequestRefund)} onClick={() => setProductMixedAction("both")} style={{ padding: "9px 12px", borderRadius: "8px", border: productMixedAction === "both" ? "1px solid #1d4ed8" : "1px solid #93c5fd", background: productState.transactionContext.linkedBookings.some((row) => row.cancellationEligibility && !row.cancellationEligibility.canRequestRefund) ? "#e5e7eb" : productMixedAction === "both" ? "#1d4ed8" : "white", color: productState.transactionContext.linkedBookings.some((row) => row.cancellationEligibility && !row.cancellationEligibility.canRequestRefund) ? "#6b7280" : productMixedAction === "both" ? "white" : "#1d4ed8", fontWeight: 600, cursor: productState.transactionContext.linkedBookings.some((row) => row.cancellationEligibility && !row.cancellationEligibility.canRequestRefund) ? "not-allowed" : "pointer" }}>
                        Return Product + Cancel Booking
                      </button>
                    </div>
                  </div>
                )}
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
                  <button
                    type="button"
                    onClick={() => setSubmissionMode("single")}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "999px",
                      border: productState.submissionMode === "single" ? "1px solid #166534" : "1px solid #cbd5e1",
                      background: productState.submissionMode === "single" ? "#dcfce7" : "white",
                      color: "#166534",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Single return
                  </button>
                  <button
                    type="button"
                    onClick={() => setSubmissionMode("batch")}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "999px",
                      border: productState.submissionMode === "batch" ? "1px solid #1d4ed8" : "1px solid #cbd5e1",
                      background: productState.submissionMode === "batch" ? "#dbeafe" : "white",
                      color: "#1d4ed8",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Batch return
                  </button>
                </div>

                <div style={{ fontSize: "12px", color: "#475569", marginBottom: "10px", lineHeight: "1.5" }}>
                  {productState.submissionMode === "batch"
                    ? "Batch return lets you submit one request that OGESTYLE automatically splits into seller-specific cases."
                    : "Single return keeps this submission to one item only."}
                </div>

                {groupedProductItems.map((group) => (
                  <div key={group.key} style={{ borderTop: "1px solid #e5e7eb", paddingTop: "10px", marginTop: "10px" }}>
                    <div style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a" }}>{group.shopName}</div>
                    <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                      {group.shopLocationName}
                      {group.shopAddress ? ` · ${group.shopAddress}` : ""}
                    </div>
                    {group.items.map((item, idx) => {
                      const rowId = item.orderItemId || `row-${idx}`;
                      const isSelected = Boolean(productState.selectedItems[item.orderItemId]);
                      const badge = getItemReturnBadge(item);
                      const locked = isItemSelectionLocked(item.orderItemId);
                      return (
                        <div key={`${group.key}-${rowId}-${idx}`} style={{ marginTop: "10px", padding: "10px", borderRadius: "8px", background: locked ? "#f8fafc" : "white", border: "1px solid #e2e8f0" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                            <div>
                              <div style={{ fontSize: "14px", fontWeight: 600 }}>{item.name}</div>
                              <div style={{ fontSize: "12px", color: "#64748b" }}>
                                Purchased Qty: {item.quantity} · Unit Price: ${item.priceAtPurchase.toFixed(2)}
                              </div>
                              <div style={{ fontSize: "13px", color: "#166534", fontWeight: 700, marginTop: "4px" }}>
                                Expected refund: ${Number(item.expectedRefundAmount || 0).toFixed(2)}
                              </div>
                              {Number(item.stripeFeeDeduction || 0) > 0 && (
                                <div style={{ fontSize: "12px", color: "#92400e", marginTop: "2px" }}>
                                  Processing fee not refundable: ${Number(item.stripeFeeDeduction || 0).toFixed(2)}
                                </div>
                              )}
                            </div>
                            {badge && (
                              <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: badge.locked ? "#92400e" : "#1d4ed8", background: badge.locked ? "#fffbeb" : "#eff6ff", border: badge.locked ? "1px solid #fde68a" : "1px solid #bfdbfe", borderRadius: "999px", padding: "4px 8px", alignSelf: "flex-start" }}>
                                {badge.label}
                                {badge.caseNumber ? ` · ${badge.caseNumber}` : ""}
                              </div>
                            )}
                          </div>
                          <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
                            <input
                              type={productState.submissionMode === "single" ? "radio" : "checkbox"}
                              name={productState.submissionMode === "single" ? "single-return-item" : undefined}
                              checked={isSelected}
                              disabled={locked}
                              onChange={(e) => toggleProductItem(item.orderItemId, e.target.checked)}
                            />
                            <label style={{ fontSize: "12px", color: locked ? "#94a3b8" : "#334155" }}>
                              {locked
                                ? "This item already has an active return case and cannot be submitted again right now."
                                : "Select this item for return (full purchased quantity)"}
                            </label>
                          </div>
                          {productState.submissionMode === "batch" && isSelected && (
                            <div style={{ marginTop: "10px", borderTop: "1px dashed #cbd5e1", paddingTop: "10px" }}>
                              <label style={{ display: "block", marginBottom: "6px", fontWeight: 600, fontSize: "12px" }}>
                                Item return reason
                              </label>
                              <input
                                type="text"
                                value={productState.itemDetailsById?.[item.orderItemId]?.reason || ""}
                                onChange={(e) =>
                                  updateItemDetails(item.orderItemId, { reason: e.target.value })
                                }
                                placeholder="Wrong item, damaged, defective, etc."
                                style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px", boxSizing: "border-box", marginBottom: "8px" }}
                              />
                              <label style={{ display: "block", marginBottom: "6px", fontWeight: 600, fontSize: "12px" }}>
                                Item description (optional)
                              </label>
                              <textarea
                                value={productState.itemDetailsById?.[item.orderItemId]?.description || ""}
                                onChange={(e) =>
                                  updateItemDetails(item.orderItemId, { description: e.target.value })
                                }
                                style={{ width: "100%", minHeight: "54px", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px", boxSizing: "border-box", marginBottom: "8px" }}
                              />

                              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "8px", marginBottom: "8px" }}>
                                <select
                                  value={productState.itemDetailsById?.[item.orderItemId]?.conditionAttestation?.itemUsedStatus || "unknown"}
                                  onChange={(e) =>
                                    updateItemDetails(item.orderItemId, {
                                      conditionAttestation: {
                                        ...(productState.itemDetailsById?.[item.orderItemId]?.conditionAttestation || {}),
                                        itemUsedStatus: e.target.value,
                                      },
                                    })
                                  }
                                  style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px", boxSizing: "border-box" }}
                                >
                                  <option value="unknown">Used? Unknown</option>
                                  <option value="unused">Used? No</option>
                                  <option value="used">Used? Yes</option>
                                </select>
                                <select
                                  value={productState.itemDetailsById?.[item.orderItemId]?.conditionAttestation?.packageOpenedStatus || "unknown"}
                                  onChange={(e) =>
                                    updateItemDetails(item.orderItemId, {
                                      conditionAttestation: {
                                        ...(productState.itemDetailsById?.[item.orderItemId]?.conditionAttestation || {}),
                                        packageOpenedStatus: e.target.value,
                                      },
                                    })
                                  }
                                  style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px", boxSizing: "border-box" }}
                                >
                                  <option value="unknown">Opened? Unknown</option>
                                  <option value="unopened">Opened? No</option>
                                  <option value="opened">Opened? Yes</option>
                                </select>
                                <select
                                  value={productState.itemDetailsById?.[item.orderItemId]?.conditionAttestation?.itemDamagedStatus || "unknown"}
                                  onChange={(e) =>
                                    updateItemDetails(item.orderItemId, {
                                      conditionAttestation: {
                                        ...(productState.itemDetailsById?.[item.orderItemId]?.conditionAttestation || {}),
                                        itemDamagedStatus: e.target.value,
                                      },
                                    })
                                  }
                                  style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px", boxSizing: "border-box" }}
                                >
                                  <option value="unknown">Damaged? Unknown</option>
                                  <option value="not_damaged">Damaged? No</option>
                                  <option value="damaged">Damaged? Yes</option>
                                </select>
                              </div>
                              <textarea
                                value={productState.itemDetailsById?.[item.orderItemId]?.conditionAttestation?.reasonDetail || ""}
                                onChange={(e) =>
                                  updateItemDetails(item.orderItemId, {
                                    conditionAttestation: {
                                      ...(productState.itemDetailsById?.[item.orderItemId]?.conditionAttestation || {}),
                                      reasonDetail: e.target.value,
                                    },
                                  })
                                }
                                placeholder="Condition details"
                                style={{ width: "100%", minHeight: "54px", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px", boxSizing: "border-box" }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}

                <div style={{ marginTop: "14px" }}>
                  {productState.submissionMode === "single" && (
                    <>
                  <label style={{ display: "block", marginBottom: "6px", fontWeight: 600 }}>Return Reason</label>
                  <input type="text" value={productState.reason} onChange={(e) => updateProduct({ reason: e.target.value })} placeholder="Wrong item, damaged, defective, etc." style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "10px", boxSizing: "border-box", marginBottom: "10px" }} />
                  <label style={{ display: "block", marginBottom: "6px", fontWeight: 600 }}>Description (optional)</label>
                  <textarea value={productState.description} onChange={(e) => updateProduct({ description: e.target.value })} style={{ width: "100%", minHeight: "76px", border: "1px solid #d1d5db", borderRadius: "8px", padding: "10px", boxSizing: "border-box", marginBottom: "10px" }} />

                  <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px", background: "#f8fafc", marginBottom: "10px" }}>
                    <div style={{ fontWeight: 700, marginBottom: "8px", fontSize: "13px", color: "#1f2937" }}>
                      Item Condition Details
                    </div>

                    {isDamagedOnlyPolicy && (
                      <div style={{ marginBottom: "8px", fontSize: "12px", color: "#9a3412", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: "6px", padding: "8px" }}>
                        Seller policy requires damaged/incorrect item claims. Select <strong>Damaged</strong> below before submitting.
                      </div>
                    )}

                    <label style={{ display: "block", marginBottom: "6px", fontWeight: 600, fontSize: "13px" }}>Was the item used?</label>
                    <select
                      value={productState.conditionAttestation?.itemUsedStatus || "unknown"}
                      onChange={(e) =>
                        updateProduct({
                          conditionAttestation: {
                            ...(productState.conditionAttestation || {}),
                            itemUsedStatus: e.target.value,
                          },
                        })
                      }
                      style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px", boxSizing: "border-box", marginBottom: "8px" }}
                    >
                      <option value="unknown">Unknown / Prefer not to say</option>
                      <option value="unused">Unused</option>
                      <option value="used">Used</option>
                    </select>

                    <label style={{ display: "block", marginBottom: "6px", fontWeight: 600, fontSize: "13px" }}>Was the package opened?</label>
                    <select
                      value={productState.conditionAttestation?.packageOpenedStatus || "unknown"}
                      onChange={(e) =>
                        updateProduct({
                          conditionAttestation: {
                            ...(productState.conditionAttestation || {}),
                            packageOpenedStatus: e.target.value,
                          },
                        })
                      }
                      style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px", boxSizing: "border-box", marginBottom: "8px" }}
                    >
                      <option value="unknown">Unknown / Prefer not to say</option>
                      <option value="unopened">Unopened</option>
                      <option value="opened">Opened</option>
                    </select>

                    <label style={{ display: "block", marginBottom: "6px", fontWeight: 600, fontSize: "13px" }}>Is the item damaged?</label>
                    <select
                      value={productState.conditionAttestation?.itemDamagedStatus || "unknown"}
                      onChange={(e) =>
                        updateProduct({
                          conditionAttestation: {
                            ...(productState.conditionAttestation || {}),
                            itemDamagedStatus: e.target.value,
                          },
                        })
                      }
                      style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px", boxSizing: "border-box", marginBottom: "8px" }}
                    >
                      <option value="unknown">Unknown / Prefer not to say</option>
                      <option value="not_damaged">Not damaged</option>
                      <option value="damaged">Damaged</option>
                    </select>

                    <label style={{ display: "block", marginBottom: "6px", fontWeight: 600, fontSize: "13px" }}>Condition details</label>
                    <textarea
                      value={productState.conditionAttestation?.reasonDetail || ""}
                      onChange={(e) =>
                        updateProduct({
                          conditionAttestation: {
                            ...(productState.conditionAttestation || {}),
                            reasonDetail: e.target.value,
                          },
                        })
                      }
                      placeholder="Provide condition specifics for faster review."
                      style={{ width: "100%", minHeight: "64px", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px", boxSizing: "border-box" }}
                    />
                  </div>
                    </>
                  )}
                </div>

                {!isSignedIn && (
                  <div style={{ border: "1px solid #fde68a", borderRadius: "8px", background: "#fffbeb", padding: "12px", marginTop: "8px", marginBottom: "10px" }}>
                    <div style={{ fontWeight: 700, marginBottom: "8px", color: "#92400e" }}>Guest Identity Check</div>
                    <label style={{ display: "block", marginBottom: "6px", fontWeight: 600 }}>Phone Last 4</label>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                      <input type="text" value={productState.phoneLast4} onChange={(e) => updateProduct({ phoneLast4: e.target.value.replace(/\D/g, "").slice(0, 4) })} style={{ width: "140px", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px" }} />
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <button type="button" onClick={() => {
                    if (productState.transactionContext?.isMixedTransaction && productMixedAction === "both") {
                      submitProductAndServiceTogether();
                      return;
                    }
                    submitProductReturnCase(!productState.transactionContext?.isMixedTransaction || productMixedAction === "product_only");
                  }} disabled={productState.submitting || (productState.transactionContext?.isMixedTransaction && !productMixedAction)} style={{ padding: "10px 14px", background: productState.submitting || (productState.transactionContext?.isMixedTransaction && !productMixedAction) ? "#9ca3af" : "#15803d", color: "#fff", border: "none", borderRadius: "8px", fontWeight: 600, cursor: productState.submitting || (productState.transactionContext?.isMixedTransaction && !productMixedAction) ? "not-allowed" : "pointer" }}>{productState.submitting ? "Submitting..." : productMixedAction === "both" ? "Return Product + Cancel Booking" : "Submit Product Return"}</button>
                  <button type="button" onClick={loadReturnCases} disabled={productState.casesLoading} style={{ padding: "10px 14px", background: "#0f172a", color: "#fff", border: "none", borderRadius: "8px", fontWeight: 600, cursor: "pointer" }}>{productState.casesLoading ? "Loading..." : "Load Return Cases"}</button>
                  <button type="button" onClick={() => resetProductFlow()} style={{ padding: "10px 14px", background: "white", color: "#6b7280", border: "1px solid #cbd5e1", borderRadius: "8px", fontWeight: 600, cursor: "pointer" }}>Cancel and Start Over</button>
                </div>
              </div>
            )}

            {productState.order && !productState.duplicateCase && productState.lookupIneligibleMessage && (
              <div style={{ border: "1px solid #fecaca", borderRadius: "8px", padding: "14px", background: "#fff1f2", marginBottom: "14px" }}>
                <div style={{ fontWeight: 700, marginBottom: "8px", color: "#991b1b" }}>Return Not Eligible</div>
                <div style={{ fontSize: "14px", color: "#7f1d1d", marginBottom: "8px" }}>{productState.lookupIneligibleMessage}</div>
                <div style={{ fontSize: "12px", color: "#9f1239" }}>
                  Order: {productState.order.orderNumber}
                </div>
              </div>
            )}

            {productState.lookupNotice && (
              <div style={{ padding: "10px", borderRadius: "8px", background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412", marginBottom: "12px" }}>
                <div>{productState.lookupNotice}</div>
              </div>
            )}

            {productState.cases.length > 0 && (
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px", marginBottom: "10px" }}>
                <div style={{ fontWeight: 700, marginBottom: "8px" }}>Your Return Cases</div>
                {productState.cases.map((row) => (
                  <div key={row._id || row.caseNumber} style={{ borderTop: "1px solid #e5e7eb", paddingTop: "8px", marginTop: "8px", fontSize: "14px" }}>
                    <div>Case: {row.caseNumber}</div>
                    <div>Status: {row.status}</div>
                    <div>Requested: ${Number(row.financial?.amountRequested || 0).toFixed(2)}</div>
                    {Number(row.financial?.stripeFeeDeduction || 0) > 0 && (
                      <div style={{ color: "#92400e", fontSize: "13px" }}>
                        Processing fee not refundable: ${Number(row.financial.stripeFeeDeduction || 0).toFixed(2)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {productState.error && (
              <div style={{ padding: "10px", borderRadius: "8px", background: "#fee2e2", border: "1px solid #fecaca", color: "#991b1b", marginBottom: "12px" }}>
                <div>{productState.error}</div>
                {productState.duplicateCase?.caseNumber && (
                  <div style={{ marginTop: "10px", display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                    <button
                      type="button"
                      onClick={handleViewExistingCase}
                      style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #ef4444", background: "#fff", color: "#991b1b", fontWeight: 600, cursor: "pointer" }}
                    >
                      View Existing Case
                    </button>
                    <span style={{ fontSize: "12px", color: "#7f1d1d" }}>
                      Case: {productState.duplicateCase.caseNumber}
                    </span>
                  </div>
                )}
              </div>
            )}
            {productState.successMessage && <div style={{ padding: "10px", borderRadius: "8px", background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534", marginBottom: "12px" }}>{productState.successMessage}</div>}
          </>
        )}
      </div>

      <CustomerFooter />
    </>
  );
}

export default GuestRefundRequest;
